// Client-side utility functions for authentication

import { signIn as nextAuthSignIn } from 'next-auth/react';

// Define constants for production vs development
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const AUTH_TIMEOUT = IS_PRODUCTION ? 30000 : 15000; // Longer timeout for production

// Define a type for our sign-in result
type SignInResult = 
  | { success: true; url: string }
  | { success: false; error: string };

// Function to handle user sign in
export async function signInUser(email: string, password: string, redirectUrl: string = 'https://contracts.saht.store'): Promise<SignInResult> {
  try {
    // Add a timeout to the sign-in process
    const signInPromise = nextAuthSignIn('credentials', {
      email,
      password,
      callbackUrl: redirectUrl,
      redirect: false, // Handle redirection manually for better error handling
    });
    
    // Create a timeout promise with environment-specific timeout
    const timeoutPromise = new Promise<{ ok?: boolean; url?: string; error?: string }>((_, reject) => {
      setTimeout(() => reject(new Error('Sign in request timed out')), AUTH_TIMEOUT);
    });
    
    // Race the sign-in promise against the timeout
    const response = await Promise.race([signInPromise, timeoutPromise]);

    if (!response?.ok) {
      return { 
        success: false, 
        error: response?.error || 'Invalid email or password' 
      };
    }

    // If the sign-in was successful, return the URL to redirect to
    return { 
      success: true,
      url: response?.url || redirectUrl
    };
  } catch (error) {
    console.error('Sign in error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'An error occurred during sign in.'
    };
  }
}